/**
 * useHoverIntentTooltip
 *
 * Controlled tooltip state with a hover-intent delay. Used by both the
 * dynamic-content rows in `WorkflowInputPanel` and the function-picker rows
 * in `FunctionPickerPanel` — both surfaces need:
 *
 *  - a single "open key/name" piece of state (so only one tooltip renders),
 *  - a delay before showing (casual mouse-throughs shouldn't pop tooltips
 *    while the user is moving toward the group header),
 *  - a synchronous force-close path (HTML5 drag captures the pointer and
 *    suppresses the synthetic `mouseleave` Fluent normally uses to dismiss,
 *    so the tooltip can otherwise float over the drop target).
 *
 * The hook is keyed on a string identifier (token key / function name) so
 * callers can compare equality cheaply in their `visible` computation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface HoverIntentTooltip {
  /** The currently-open key, or null if no tooltip is showing. */
  readonly openKey: string | null;
  /** Schedule a tooltip show after `delayMs`. Cancels any in-flight schedule. */
  readonly scheduleShow: (key: string) => void;
  /** Cancel any pending show *and* close the tooltip if open. */
  readonly close: () => void;
  /**
   * Setter for the open key. Exposed so callers can implement the "honour
   * close-only events from Fluent (Esc, focus moves) but ignore opens" guard
   * inside their `onVisibleChange` without re-implementing the cancel timer.
   */
  readonly setOpenKey: (key: string | null) => void;
}

export function useHoverIntentTooltip(delayMs: number): HoverIntentTooltip {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const cancelShow = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleShow = useCallback(
    (key: string) => {
      cancelShow();
      timerRef.current = window.setTimeout(() => {
        setOpenKey(key);
        timerRef.current = null;
      }, delayMs);
    },
    [cancelShow, delayMs],
  );

  const close = useCallback(() => {
    cancelShow();
    setOpenKey(null);
  }, [cancelShow]);

  useEffect(() => () => cancelShow(), [cancelShow]);

  return { openKey, scheduleShow, close, setOpenKey };
}
